/**
 * The "Home" store.
 * This is the store that keeps common version information for the application.
 *
 * File: \src\views\HomeStore.ts
 * Created Date: Thursday, April 28th 2022, 09:07:38
 * Author: Kircheva Boryana (HAU-EDCP)
 * -----
 * Last Modified: November 22nd 2024, 07:33:42
 * Last Modified: November 22nd 2024, 07:33:42
 * Modified By: Herrmann Sascha (LDC)
 * -----
 * Copyright (c) 2017-2018 Liebherr-Hausgeräte GmbH
 */

import { defineStore } from 'pinia';

import { CubeLogger } from '@/Logger';

import { ConfigControllerApi, GetCheckedServerVersionUsingGETRequest, MessagingControllerApi } from '@/api';
import { useApiStore } from '@/api/ApiStore';
import { useConfigStore } from '@/components/config/ConfigStore';
import { useLoginStore } from '@/components/login/LoginStore';
import { useUpdateStore } from '@/components/update/ui/UpdateStore';

// Interface for the root store
export interface RootState {
  versionFrontend: string;
  versionBackend: string;
  messageWriteAccess: boolean;
}

export const useHomeStore = defineStore('homeStore', {
  /**
   * Initialize state.
   */
  state: (): RootState => ({
    versionFrontend: '?',
    versionBackend: '?',
    messageWriteAccess: false
  }),

  getters: {
    /**
     * Getter for the UI version. It is based on the build number coming from Jenkins.
     *
     * DEV, INT: Just the build number
     * STABLE/PRODUCTION: 1.0.<build_number>
     *
     * @returns {string} the version.
     */
    getFrontendVersion(): string {
      return this.versionFrontend;
    },

    /**
     * Getter for the backend version. It is based on the build number coming from Jenkins.
     *
     * DEV, INT: Just the build number
     * STABLE/PRODUCTION: 1.0.<build_number>
     *
     * @returns {string} the version.
     */
    getBackendVersion(): string {
      return this.versionBackend;
    },

    /**
     * Getter for boolean to show/hide newsCenter
     *
     * @param {RootState} state
     * @returns {boolean}
     */
    hasMessageWriteAccess(): boolean {
      return this.messageWriteAccess;
    }
  },

  actions: {
    /**
     * Stores the versions by reading them from the server and then setting them.
     *
     * @param {string} uiVersion The UI version to store.
     */
    readVersions(): void {
      const logger = CubeLogger.$log.prefix('HomeStore::readVersions');
      logger.debug('Reading software versions from the backend');

      const apiStore = useApiStore();
      const configControllerApi: ConfigControllerApi = apiStore.getConfigControllerApi;

      const version: string = document.getElementsByName('bus_ui_version')[0].getAttribute('content');

      const request: GetCheckedServerVersionUsingGETRequest = {
        frontendVersion: version
      };

      configControllerApi
        .getCheckedServerVersionUsingGET(request)
        .then(response => {
          this.storeVersions(response.frontendVersion, response.backendVersion);
        })
        .catch(error => {
          logger.error('Cannot read the versions from the server', error);
        });
    },

    /**
     * Stores the writeAccess for messages
     *
     * @param {*} {commit, rootGetters}
     */
    readMessageRights(): void {
      const logger = CubeLogger.$log.prefix('HomeStore::readMessageRights');
      logger.debug('Reading message rights from the backend');

      const apiStore = useApiStore();
      const messagingControllerApi: MessagingControllerApi = apiStore.getMessagingControllerApi;

      messagingControllerApi
        .hasWriteAccessUsingGET()
        .then(response => {
          this.storeMessageWriteAccess(response);
        })
        .catch(error => {
          logger.error('Cannot read the rights from the server', error);
        });
    },

    /**
     * Stores the version we read.
     *
     * @param {string} frontendVersion The frontend version.
     * @param {string} backendVersion The backend version.
     */
    storeVersions(frontendVersion: string, backendVersion: string): void {
      const logger = CubeLogger.$log.prefix('HomeStore::storeVersions');
      logger.debug('Frontend: ' + frontendVersion + '; Backend: ' + backendVersion);

      this.versionFrontend = frontendVersion;
      this.versionBackend = backendVersion;
    },

    /**
     * Handles the right to edit messages
     *
     * @param {RootState} state The state.
     * @param {boolean} messageRights The right to edit messages.
     */
    storeMessageWriteAccess(writeAccess: boolean): void {
      this.messageWriteAccess = writeAccess;
    },

    /**
     * Inits the stores.
     */
    initStores(): void {
      useApiStore();
      useConfigStore();
      useLoginStore();
      useUpdateStore();
    }
  }
});
